import {
  AppWindow,
  BarChart3,
  Cloud,
  Code2,
  Cpu,
  Database,
  LayoutDashboard,
  Megaphone,
  Palette,
  Rocket,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Workflow,
} from 'lucide-react';
import type { CaseStudy, NavItem, Service, Stat } from '../types/content';

export const navItems: NavItem[] = [
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Contact', href: '/contact' },
];

export const stats: Stat[] = [
  { value: '360+', label: 'successful projects' },
  { value: '100%', label: 'delivery success ratio' },
  { value: '5,000+', label: 'web pages designed' },
  { value: '10+', label: 'years of experience' },
];

export const services: Service[] = [
  {
    title: 'Application Development',
    summary:
      'Custom software platforms, portals, and workflow systems built around business operations rather than generic templates.',
    outcomes: ['Modern React, Node, .NET, PHP and Angular stacks', 'Secure role-based dashboards', 'Workflow automation and reporting'],
    icon: Code2,
  },
  {
    title: 'Website Design & Development',
    summary:
      'Conversion-ready websites and portals with stronger content structure, responsive design, and maintainable front-end architecture.',
    outcomes: ['Corporate websites and web portals', 'Accessible content systems', 'Ongoing maintenance and optimization'],
    icon: AppWindow,
  },
  {
    title: 'Mobile App Development',
    summary:
      'Native and cross-platform mobile applications for Android and iOS, engineered for usability, security, and long-term scale.',
    outcomes: ['React Native and native delivery', 'Cross-device experience design', 'Secure mobile app workflows'],
    icon: Smartphone,
  },
  {
    title: 'Ecommerce Development',
    summary:
      'Digital storefronts designed to increase product discovery, simplify checkout, and support sustainable operational growth.',
    outcomes: ['Product catalogs and inventory', 'Secure payment integrations', 'Customer loyalty and analytics'],
    icon: ShoppingCart,
  },
  {
    title: 'Cloud Services',
    summary:
      'Cloud assessment, migration, CI/CD, backup, security, support, and modernization across AWS, Azure, Google Cloud, and Oracle Cloud.',
    outcomes: ['Cloud readiness strategy', 'DevOps and CI/CD pipelines', 'Backup, recovery, and storage management'],
    icon: Cloud,
  },
  {
    title: 'Digital Marketing & Design',
    summary:
      'Brand, UI, graphic, and digital marketing support to keep software launches clear, attractive, and measurable.',
    outcomes: ['Logo and interface design', 'Campaign-ready landing pages', 'Analytics-backed growth experiments'],
    icon: Megaphone,
  },
  {
    title: 'Low-Code / No-Code Solutions',
    summary:
      'Rapid application delivery through low-code automation platforms for teams that need business value without lengthy build cycles.',
    outcomes: ['Faster MVPs and internal tools', 'Automated application lifecycle', 'Process digitization'],
    icon: Workflow,
  },
];

export const capabilities = [
  { label: 'Cloud Assessment', icon: BarChart3 },
  { label: 'Compliance & Security', icon: ShieldCheck },
  { label: 'Storage Management', icon: Database },
  { label: 'CI/CD Delivery', icon: Rocket },
  { label: 'Low-Code Automation', icon: Cpu },
  { label: 'UX & Interface Design', icon: Palette },
  { label: 'Analytics Dashboards', icon: LayoutDashboard },
];

export const caseStudies: CaseStudy[] = [
  {
    client: 'Automotive Manufacturer',
    title: 'Automating pre-production processes to reduce delays and losses',
    problem:
      'The client relied on manual scheduling and vendor coordination for pre-production work, creating delays, quality variance, and limited operational visibility.',
    solution: [
      'Centralized process platform with real-time status tracking',
      'Automated RFQ intake and lifecycle workflows',
      'Third-party vendor coordination and custom reporting',
    ],
    impact: [
      'Improved on-time delivery for pre-production parts',
      'Reduced manual dependency and error risk',
      'Better visibility into issues, risks, and process bottlenecks',
    ],
  },
  {
    client: 'Swativweek Mart',
    title: 'Mobile and web platform for grocery operations',
    problem:
      'A local grocery chain needed better inventory control, customer engagement, and sales tracking across stores.',
    solution: [
      'Real-time inventory tracking across locations',
      'Personalized offers and loyalty rewards',
      'Scalable web and mobile access with analytics dashboards',
    ],
    impact: [
      'Streamlined operations and stock visibility',
      'Improved customer loyalty through targeted experiences',
      'Data-backed decision-making for growth planning',
    ],
  },
  {
    client: 'Sahyadri Farm',
    title: 'Ecommerce platform for organic produce',
    problem:
      'The farm relied on traditional retail channels, limiting geographic reach, market visibility, and customer convenience.',
    solution: [
      'Responsive ecommerce website for organic product discovery',
      'Secure payment gateway and inventory management',
      'Analytics and reporting to support continuous improvement',
    ],
    impact: [
      'Expanded customer access beyond local retail channels',
      'Simplified ordering and doorstep delivery',
      'Stronger product visibility for sustainable agriculture offerings',
    ],
  },
];

export const engagementModels = [
  {
    title: 'Project-Based Engagement',
    body: 'Best for one-off, pilot, or clearly scoped initiatives where requirements, milestones, and deliverables are defined upfront.',
  },
  {
    title: 'Dedicated Development Team',
    body: 'Best for companies that need to extend internal capacity with developers, designers, project managers, and delivery infrastructure.',
  },
];

export const contact = {
  phone: '+91 7030267771',
  email: 'contact@spitertech.com',
  website: 'www.spitertech.com',
  address: 'Ground Floor, Gurupushp Appt, near Honda Showroom, Mahatma Nagar Road, Nashik',
};
